import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { css } from '@emotion/react';
import { colors, spacing } from '../../styles';
import Modal from '../ModalPopUp/Modal';

interface RatingModalProps {
  isOpen: boolean;
  onClose: () => void;
  onLoveIt?: () => void;
  onGiveFeedback?: () => void;
  disableFeedback?: boolean;
}

/**
 * Modal to request user feedback and redirect them to store or feedback form
 */
const RatingModal: React.FC<RatingModalProps> = ({
  isOpen,
  onClose,
  onLoveIt,
  onGiveFeedback,
  disableFeedback = false,
}) => {
  const { t } = useTranslation();
  const [isDarkMode, setIsDarkMode] = useState(document.documentElement.getAttribute('data-theme') === 'dark');

  // Handle theme changes
  useEffect(() => {
    const handleThemeChange = () => {
      setIsDarkMode(document.documentElement.getAttribute('data-theme') === 'dark');
    };

    handleThemeChange();

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'data-theme') {
          handleThemeChange();
        }
      });
    });

    observer.observe(document.documentElement, { attributes: true });

    return () => observer.disconnect();
  }, []);

  const handleLoveIt = () => {
    if (onLoveIt) {
      onLoveIt();
    } else {
      onClose();
    }
  };

  const handleGiveFeedback = () => {
    if (onGiveFeedback) {
      onGiveFeedback();
    } else {
      onClose();
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={t('rating.title')} maxWidth="300px">
      <div
        css={css`
          padding: ${spacing.deci};
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: ${spacing.deci};
        `}
      >
        {/* Feedback buttons */}
        <button
          onClick={handleLoveIt}
          css={css`
            background-color: ${colors.petrolBlue};
            color: white;
            border: none;
            border-radius: 8px;
            padding: ${spacing.milli} ${spacing.deci};
            font-size: 16px;
            font-weight: 500;
            cursor: pointer;
            width: 100%;
            transition: transform 0.2s;

            &:hover {
              transform: translateY(-2px);
            }
          `}
        >
          {t('rating.loveIt')}
        </button>

        <button
          onClick={handleGiveFeedback}
          css={css`
            background-color: transparent;
            color: ${isDarkMode ? colors.sugar : colors.liquorice};
            border: 1px solid ${isDarkMode ? colors.sugar : colors.liquorice};
            border-radius: 8px;
            padding: ${spacing.milli} ${spacing.deci};
            font-size: 16px;
            font-weight: 500;
            cursor: pointer;
            width: 100%;
            transition: transform 0.2s;

            &:hover {
              transform: translateY(-2px);
            }
          `}
        >
          {disableFeedback ? t('rating.notNow') : t('rating.giveFeedback')}
        </button>
      </div>
    </Modal>
  );
};

export default RatingModal;
